/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'

import { EuiButton, EuiFlexGroup, EuiFlexItem, EuiPanel, EuiSpacer, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { StackDeployment } from '@modules/ui-types'

import {
  hasRecentSnapshotSuccess,
  getScheduledSnapshotTime,
  getLatestSnapshotSuccess,
} from '@/lib/stackDeployments/selectors/snapshots'
import {
  getFirstSliderClusterFromGet,
  getFirstEsClusterFromGet,
} from '@/lib/stackDeployments/selectors/fundamentals'

import ExternalLink from '../../../ExternalLink'
import SnapshotStat, { DateStat } from '../SnapshotStatus/SnapshotStat'
import {
  kibanaSnapshotAndRestoreUrl,
  kibanaSnapshotPolicyUrl,
} from '../../../../lib/serviceProviderDeepLinks'

import type { FunctionComponent } from 'react'

type Props = {
  deployment: StackDeployment
}

const SlmSnapshotStatus: FunctionComponent<Props> = ({ deployment }) => {
  const resource = getFirstEsClusterFromGet({ deployment })!
  const hasRecentEnoughSuccess = hasRecentSnapshotSuccess({ resource })
  const latestSuccessAt = getLatestSnapshotSuccess({ resource })
  const nextSnapshotAt = getScheduledSnapshotTime({ resource })
  const kibana = getFirstSliderClusterFromGet({
    deployment,
    sliderInstanceType: `kibana`,
  })

  return (
    <Fragment>
      <EuiFlexGroup data-test-subj='slm-snapshot-status'>
        <EuiFlexItem>
          <EuiPanel>
            <SnapshotStat
              label={
                <FormattedMessage
                  id='deployment-snapshots-status.last-successful-snapshot'
                  defaultMessage='Last successful snapshot'
                />
              }
              color={hasRecentEnoughSuccess ? undefined : `danger`}
            >
              <DateStat date={latestSuccessAt} />
            </SnapshotStat>
          </EuiPanel>
        </EuiFlexItem>

        {nextSnapshotAt && (
          <EuiFlexItem>
            <EuiPanel>
              <SnapshotStat
                label={
                  <FormattedMessage
                    id='deployment-snapshots-status.next-snapshot'
                    defaultMessage='Next snapshot'
                  />
                }
              >
                <DateStat date={nextSnapshotAt} />
              </SnapshotStat>
              <EuiSpacer size='s' />
              <EuiText size='s'>
                <ExternalLink href={kibanaSnapshotPolicyUrl({ resource: kibana })}>
                  <FormattedMessage
                    id='deployment-snapshots-status.modify-frequency'
                    defaultMessage='Modify frequency'
                  />
                </ExternalLink>
              </EuiText>
            </EuiPanel>
          </EuiFlexItem>
        )}

        <EuiFlexItem>
          <EuiPanel>
            <SnapshotStat
              label={
                <FormattedMessage
                  id='deployment-snapshots-status.snapshot-management'
                  defaultMessage='Snapshot management'
                />
              }
            >
              <EuiButton
                href={kibanaSnapshotAndRestoreUrl({ resource: kibana })}
                iconType='logoKibana'
              >
                <FormattedMessage
                  id='deployment-snapshots-status.snapshot-management-cta'
                  defaultMessage='Snapshot and Restore'
                />
              </EuiButton>
            </SnapshotStat>
            <EuiSpacer size='xs' />
            <EuiText color='subdued' size='s'>
              <FormattedMessage
                id='deployment-snapshots-status.snapshot-management-kibana'
                defaultMessage='Manage in Kibana'
              />
            </EuiText>
          </EuiPanel>
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiSpacer size='m' />
    </Fragment>
  )
}

export default SlmSnapshotStatus
